import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

// A masked text input for credentials-style values. Shape mirrors `TextField` so the rest
// of the form looks consistent, with three additions:
//
//   * Eye toggle — switches the input between `type="password"` and `type="text"` so the
//     operator can verify what they typed without having to re-paste.
//   * Clear button — wipes the field in one click. Distinct from "blank field on first
//     open" because the form's submit logic uses the cleared signal to send a NULL update
//     (vs. omitting the field, which keeps the existing value).
//   * Optional placeholder hint when empty so it's obvious the field accepts the same
//     `env:VAR` / `raw:VALUE` / `key:VALUE` schemes the backend resolves.
//
// The actual revealed value never leaves the browser; the masked rendering is purely a
// visual courtesy. Server-side the field is returned to authenticated operators (see SDL).

export interface SecretFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  placeholder?: string | undefined;
  /** When true the eye/clear affordances are still rendered but the input is disabled. */
  disabled?: boolean | undefined;
}

export function SecretField({
  label,
  value,
  onChange,
  hint,
  error,
  placeholder,
  disabled,
}: SecretFieldProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const id = useId();
  const describedById = `${id}-desc`;
  const [revealed, setRevealed] = useState(false);
  const hasValue = value.length > 0;

  const inputStateClasses = error
    ? isDark
      ? 'border-rose-500/40 bg-rose-500/[0.04]'
      : 'border-rose-300 bg-rose-50/40'
    : isDark
      ? 'border-white/[0.08] bg-white/[0.04] text-white placeholder-white/30 focus:border-indigo-500'
      : 'border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:border-indigo-500';

  const iconButtonClasses = isDark
    ? 'text-white/40 hover:text-white/80'
    : 'text-slate-400 hover:text-slate-700';

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className={`block text-xs font-medium ${isDark ? 'text-white/60' : 'text-slate-600'}`}
      >
        {label}
      </label>

      <div className="relative">
        <input
          id={id}
          type={revealed ? 'text' : 'password'}
          autoComplete="off"
          spellCheck={false}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? describedById : undefined}
          className={`min-h-11 w-full rounded-xl border py-2.5 pl-4 text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 ${
            // Reserve right-side space for the action buttons; one slot for eye, one for clear.
            hasValue ? 'pr-20' : 'pr-12'
          } ${inputStateClasses}`}
        />

        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
          {hasValue && (
            <button
              type="button"
              // Generic labels (without the field label text) keep the SR experience clear
              // while avoiding name collisions with `getByLabelText(/<field>/i)` queries —
              // testing-library matches across role types so duplicating the field label on
              // sibling buttons surfaces multiple-match errors in unrelated form tests.
              aria-label="Clear value"
              data-testid="secret-field-clear"
              title="Clear"
              disabled={disabled}
              onClick={() => onChange('')}
              className={`pointer-events-auto rounded-md p-1.5 transition-colors ${iconButtonClasses}`}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            aria-label={revealed ? 'Hide value' : 'Show value'}
            aria-pressed={revealed}
            data-testid="secret-field-toggle"
            title={revealed ? 'Hide' : 'Show'}
            disabled={disabled}
            onClick={() => setRevealed((r) => !r)}
            className={`pointer-events-auto rounded-md p-1.5 transition-colors ${iconButtonClasses}`}
          >
            {revealed ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {error ? (
        <p id={describedById} className={`text-xs ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>
          {error}
        </p>
      ) : hint ? (
        <p id={describedById} className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
